package ui.home

import androidx.compose.runtime.*
import api.ResourceType
import common.frontend.api.*
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabDefinition
import ui.app.assign.AppAssignListingArgs
import ui.app.rss.AppRssListingArgs
import ui.app.visibility.AppVisibilityListingArgs
import ui.campaign.CampaignListingArgs
import ui.company.detail.CompanyArgs
import ui.dashboard.ArgsDashboard
import ui.device.DeviceListingArgs
import ui.location.LocationListingArgs
import ui.resource.ResourceListingArgs
import ui.user.UserListingArgs
import ui.user.detail.UserArgs

@VM
class HomeVM : NavigationViewModel() {

    private lateinit var scopeUi: HomeScope

    var navigationItems by mutableDataState<List<TabDefinition>>()
        private set

    var navigationItem: String by mutableStateOf("")
        private set

    sealed interface Action {

        companion object {

            @Composable
            fun rememberAction(vm: HomeVM): (Action) -> Unit {
                return remember {
                    { act ->

                    }
                }
            }
        }
    }

    fun init(scopeUi: HomeScope) {
        this.scopeUi = scopeUi
        if (navigationItems is DataState.Result) return



        scope.launch {
            scopeUi.account.reload(
                onFail = { window.location.reload() },
                onSuccess = { login ->
                    scopeUi.vmGlobal.reloadCompanyWithLocation(login)
                    val tabVariants = TabVariants(
                        user = login,
                        nav = scopeUi.nav
                    ) {
                        navigationItem = it
                    }
                    val navItems = buildNavigationPanel(tabVariants, login)
                    navigationItems = DataState.Result(navItems)
                }
            )
        }
    }

    class TabVariants(
        user: Io.User,
        val nav: Navigation,
        onClick: (tag: String) -> Unit
    ) {
        private val isSubmenuExpanded = mutableStateOf(user.id == 1006L)

        val clickAction: TabDefinition.(index: Int) -> Unit = { _ ->
            nav.findChildNavigation(Graph.HomeContent)?.also {

                onClick(tag)
                it.navigateTo(node, args = args, clearTop = true)
            }
        }

        val dashboardMy = TabDefinition(
            node = Node.DashboardUI,
            icon = Svg.View_dashboard,
            args = ArgsDashboard(
                serverId = user.server.id,
                companyId = user.company.id,
                menuEntry = true
            ).asBundle(),
            label = Res.string.home_panel__dashboard_my,
            onClick = clickAction
        )
        val dashboardAll = TabDefinition(
            node = Node.DashboardUI,
            icon = Svg.View_dashboard,
            args = ArgsDashboard(
                serverId = user.server.id,
                menuEntry = true
            ).asBundle(),
            label = Res.string.home_panel__dashboard_all,
            onClick = clickAction
        )
        val campaignMy = TabDefinition(
            node = Node.CampaignListingUI,
            icon = Svg.Animation_play,
            args = CampaignListingArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__campaign_my,
            onClick = clickAction
        )
        val campaignMyAdmin = campaignMy.copy(label = Res.string.home_panel__campaign_admin_my)


        val companyAll = TabDefinition(
            node = Node.CompanyListingUI,
            icon = Svg.Store,
            args = ArgumentsNavigation(),
            label = Res.string.home_panel__company_all,
            onClick = clickAction
        )

        val companyMy = TabDefinition(
            node = Node.CompanyUI,
            icon = Svg.Store,
            args = CompanyArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__company_my,
            onClick = clickAction
        )

        val appsMy = TabDefinition(
            node = Node.AppAssignListingUI,
            icon = Svg.Square_rounded_badge_outline,
            args = AppAssignListingArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__apps_my,
            onClick = clickAction
        )
        val appsMyAdmin = appsMy.copy(label = Res.string.home_panel__apps_admin_my)


        val mediaMy = TabDefinition(
            node = Node.ResourceListingUI,
            icon = Svg.Multimedia,
            args = ResourceListingArgs(
                companyId = user.company.id,
                restrictionType = ResourceType.UNDEFINED.mask
            ).asBundle(),
            label = Res.string.home_panel__media_my,
            onClick = clickAction
        )

        val mediaMyAdmin = mediaMy.copy(label = Res.string.home_panel__media_admin_my)


        val locationMy = TabDefinition(
            node = Node.LocationListingUI,
            icon = Svg.Marker,
            args = LocationListingArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__location_my,
            onClick = clickAction
        )

        val locationMyAdmin = locationMy.copy(label = Res.string.home_panel__location_admin_my)

        val locationAll = TabDefinition(
            node = Node.LocationListingUI,
            icon = Svg.Marker,
            args = LocationListingArgs(
                companyId = user.company.id,
                showAll = true
            ).asBundle(),
            label = Res.string.home_panel__location_all,
            onClick = clickAction
        )

        val devicesAll = TabDefinition(
            node = Node.DeviceListingUI,
            icon = Svg.Device,
            args = DeviceListingArgs(
                companyId = user.company.id,
                showAll = true
            ).asBundle(),
            label = Res.string.home_panel__device_all,
            onClick = clickAction
        )

        val devicesMy = TabDefinition(
            node = Node.DeviceListingUI,
            icon = Svg.Device,
            args = DeviceListingArgs(
                companyId = user.company.id,
                showAll = false
            ).asBundle(),
            label = Res.string.home_panel__device_my,
            onClick = clickAction
        )

        val devicesMyAdmin = devicesMy.copy(label = Res.string.home_panel__device_admin_my)

        val userMy = TabDefinition(
            node = Node.UserListingUI,
            icon = Svg.Account,
            args = UserListingArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__user_my,
            onClick = clickAction
        )

        val userMyAdmin = userMy.copy(label = Res.string.home_panel__user_admin_my)

        val userI = TabDefinition(
            node = Node.UserUI,
            icon = Svg.Account,
            args = UserArgs(user.id).asBundle(),
            label = Res.string.home_panel__user_i,
            onClick = clickAction
        )

        val userAll = TabDefinition(
            node = Node.UserListingUI,
            icon = Svg.Account,
            args = UserListingArgs(
                user.company.id,
                showAll = true
            ).asBundle(),
            label = Res.string.home_panel__user_all,
            onClick = clickAction
        )
        val serverAll = TabDefinition(
            node = Node.ServerListingUI,
            icon = Svg.Lan,
            args = ArgumentsNavigation(),
            label = Res.string.home_panel__server_all,
            onClick = clickAction
        )
        val hardware = TabDefinition(
            node = Node.HardwareListingUI,
            icon = Svg.Desktop_classic,
            args = ArgumentsNavigation(),
            label = Res.string.home_panel__hardware,
            onClick = clickAction
        )
        val translations = TabDefinition(
            node = Node.TranslationListingUI,
            icon = Svg.Translate,
            args = ArgumentsNavigation(),
            label = Res.string.home_panel__translation,
            onClick = clickAction
        )
        val appsApproval = TabDefinition(
            node = Node.AppVisibilityListingUI,
            icon = Svg.Square_rounded_badge_outline,
            args = AppVisibilityListingArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__apps_visibility,
            onClick = clickAction
        )

        val license = TabDefinition(
            node = Node.LicenseListingUI,
            icon = Svg.File_sign,
            args = ArgumentsNavigation(),
            label = Res.string.home_panel__license,
            onClick = clickAction
        )

        val rssConfiguration = TabDefinition(
            node = Node.AppRssListingUI,
            icon = Svg.Square_rounded_badge_outline,
            args = AppRssListingArgs(user.company.id).asBundle(),
            label = Res.string.home_panel__layer_bottom_rss,
            onClick = clickAction
        )

        fun administration(submenu: List<TabDefinition>): TabDefinition = TabDefinition(
            node = Node.EmptyUI,
            icon = Svg.Security,
            args = ArgumentsNavigation(),
            label = Res.string.home_panel__administration,
            expanded = isSubmenuExpanded,
            onClick = { isSubmenuExpanded.value = !isSubmenuExpanded.value },
            submenu = submenu
        )
    }


    private fun buildNavigationPanel(tabVariants: TabVariants, login: Io.User): List<TabDefinition> = buildList {
        if (login.dashboards.isNotEmpty()) {
            if (login.canCompanyView(-1)) add(tabVariants.dashboardAll)
            else add(tabVariants.dashboardMy)
        }
        if (login.isSuperAdmin() || login.isServerAdmin()) {
            if (login.canCompanyView(-1)) add(tabVariants.companyAll)
            if (login.canLayerTopView(login.company.id)) add(tabVariants.campaignMyAdmin)
            if (login.canLayerBottomView(login.company.id)) add(tabVariants.appsMyAdmin)
            if (login.canResourceView(login.company.id)) add(tabVariants.mediaMyAdmin)
            if (login.canLocationView(login.company.id)) add(tabVariants.locationMyAdmin)
            if (login.canDeviceView(login.company.id)) add(tabVariants.devicesMyAdmin)
            if (login.canUserView(login.company.id)) add(tabVariants.userMyAdmin)
            add(tabVariants.administration(buildList {
                if (login.isSuperAdmin() && login.canServerView(-1)) add(tabVariants.serverAll)
                if (login.canLayerBottomView(-1)) add(tabVariants.appsApproval)
                if (login.canLocationView(-1)) add(tabVariants.locationAll)
                if (login.canDeviceView(-1)) add(tabVariants.devicesAll)
                if (login.canUserView(-1)) add(tabVariants.userAll)
                if (login.canLicenseView()) add(tabVariants.license)
                if (login.canHardwareView()) add(tabVariants.hardware)
                if (login.isSuperAdmin()) add(tabVariants.rssConfiguration)
                if (login.isSuperAdmin() && login.canTranslationView()) add(tabVariants.translations)
            }))
        } else {
            if (login.canCompanyView(-1)) {
                add(tabVariants.companyAll)

                if (login.canLayerTopView(login.company.id)) add(tabVariants.campaignMyAdmin)
                if (login.canLayerBottomView(login.company.id)) add(tabVariants.appsMyAdmin)
                if (login.canResourceView(login.company.id)) add(tabVariants.mediaMyAdmin)
                if (login.canLocationView(login.company.id)) add(tabVariants.locationMyAdmin)
                if (login.canDeviceView(login.company.id)) add(tabVariants.devicesMyAdmin)
            } else {
                if (login.canLayerTopView(login.company.id)) add(tabVariants.campaignMy)
                if (login.canLayerBottomView(login.company.id)) add(tabVariants.appsMy)
                if (login.canResourceView(login.company.id)) add(tabVariants.mediaMy)
                if (login.canCompanyView(login.company.id)) add(tabVariants.companyMy)
                if (login.canLocationView(login.company.id)) add(tabVariants.locationMy)
                if (login.canDeviceView(login.company.id)) add(tabVariants.devicesMy)
            }

            if (login.isCompanyAdmin()) {
                if (login.canUserView(login.company.id)) {
                    if (login.canCompanyView(-1)) add(tabVariants.userMyAdmin)
                    else add(tabVariants.userMy)
                } else {
                    add(tabVariants.userI)
                }
            } else {
                if (login.canUserView(login.company.id)) add(tabVariants.userMy)
                else add(tabVariants.userI)
            }
        }
    }


    override fun onRelease() {

    }

    fun setNavigationRoot(tag: String) {
        navigationItem = tag
    }
}
